'use client';

import { Button } from '@/components/common/Button';
import { FunctionComponent, useEffect, useRef, useState } from 'react';

// The console is a dense multi-pane layout (inbox list + thread pane, side
// panels, tables) whose columns stop fitting side by side below the laptop
// floor. Gate on both axes so a short desktop window is caught too, not only
// narrow phones/tablets.
const SUPPORTED = '(min-width: 1024px) and (min-height: 600px)';

export const ViewportGate: FunctionComponent = () => {
  const [supported, setSupported] = useState(true);
  const [dismissed, setDismissed] = useState(false);
  const dialogRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    const mq = window.matchMedia(SUPPORTED);
    const sync = (): void => setSupported(mq.matches);
    sync();
    mq.addEventListener('change', sync);
    return () => mq.removeEventListener('change', sync);
  }, []);

  const blocked = !supported && !dismissed;

  useEffect(() => {
    if (!blocked) return;
    // Global shortcuts (Cmd+K palette, Cmd+Shift+M voice capture) listen on
    // window and would fire invisibly under the overlay, and keys pressed on
    // the dialog itself bubble up to them too — so stop every keydown at the
    // window capture phase. The dismiss button stays operable because Enter/
    // Space activate it via a separate click event. Tab is pinned to the
    // single focusable control so focus can't escape into the hidden app.
    const trapKeys = (e: KeyboardEvent): void => {
      e.stopPropagation();
      const dialog = dialogRef.current;
      const inside = dialog !== null && e.target instanceof Node && dialog.contains(e.target);
      if (!inside || e.key === 'Tab') {
        e.preventDefault();
        dialog?.querySelector('button')?.focus();
      }
    };
    window.addEventListener('keydown', trapKeys, { capture: true });
    const { style } = document.body;
    const previousOverflow = style.overflow;
    style.overflow = 'hidden';
    return () => {
      window.removeEventListener('keydown', trapKeys, { capture: true });
      style.overflow = previousOverflow;
    };
  }, [blocked]);

  if (!blocked) return null;

  return (
    <div
      ref={dialogRef}
      role="dialog"
      aria-modal="true"
      aria-labelledby="viewport-gate-title"
      className="fixed inset-0 z-[10000] flex items-center justify-center bg-bg p-[24px]"
    >
      <div className="flex w-full max-w-[360px] flex-col items-center gap-[14px] rounded-[var(--radius-lg)] border border-line-2 bg-bg-2 px-[28px] py-[32px] text-center shadow-[0_20px_60px_rgba(0,0,0,0.18)]">
        <svg width="52" height="52" viewBox="0 0 100 100" aria-hidden>
          <g transform="rotate(180 50 50)">
            <circle cx="50" cy="50" r="48" fill="#FDE68A" />
            <circle cx="26" cy="56" r="5" fill="#F59E0B" opacity="0.55" />
            <circle cx="74" cy="56" r="5" fill="#F59E0B" opacity="0.55" />
            <circle cx="32" cy="42" r="5" fill="#92400E" />
            <circle cx="68" cy="42" r="5" fill="#92400E" />
            <path
              d="M 36 62 Q 50 77.4 64 62"
              stroke="#92400E"
              strokeWidth="3.5"
              strokeLinecap="round"
              fill="none"
            />
          </g>
        </svg>
        <h1 id="viewport-gate-title" className="text-[16px] font-semibold text-ink">
          Best viewed on desktop
        </h1>
        <p className="text-[13px] leading-[1.5] text-ink-3">
          Matilda isn&rsquo;t built for smaller screens just yet. Open on a laptop or desktop for the full
          experience!
        </p>
        <Button variant="primary" onClick={() => setDismissed(true)} className="mt-[4px]" autoFocus>
          Continue anyway
        </Button>
      </div>
    </div>
  );
};
